// Shared accessibility helpers for navigation and other components
// Keep functions small and reusable so multiple tickets can reuse them

function setAriaExpanded(targetElement, expanded) {
  if (!targetElement) return;
  targetElement.setAttribute('aria-expanded', expanded ? 'true' : 'false');
}

function setAriaHidden(targetElement, hidden) {
  if (!targetElement) return;
  targetElement.setAttribute('aria-hidden', hidden ? 'true' : 'false');
}

function setInertState(targetElement, inert) {
  if (!targetElement) return;

  if (inert) {
    targetElement.setAttribute('inert', '');
  } else {
    targetElement.removeAttribute('inert');
  }
}

function focusFirstFocusable(container) {
  if (!container) return null;
  const focusable = container.querySelectorAll("a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])");
  if (focusable.length) { focusable[0].focus(); return focusable[0]; }
  return null;
}

function focusLastFocusable(container) {
  if (!container) return null;
  const focusable = container.querySelectorAll("a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])");
  if (focusable.length) { focusable[focusable.length - 1].focus(); return focusable[focusable.length - 1]; }
  return null;
}

/**
 * Announce a short message to an aria-live region for screen readers.
 * Defaults to `cart-live-region-text` or falls back to creating a hidden live region.
 */
function announce(message, politeness = 'polite', regionId = 'cart-live-region-text') {
  try {
    // Prefer the main cart live region by default, fall back to the drawer live region
    let region = document.getElementById(regionId) || document.getElementById('CartDrawer-LiveRegionText');
    if (!region) {
      region = document.createElement('div');
      region.id = regionId;
      region.setAttribute('aria-live', politeness);
      region.setAttribute('aria-atomic', 'true');
      region.style.position = 'absolute';
      region.style.width = '1px';
      region.style.height = '1px';
      region.style.margin = '-1px';
      region.style.border = '0';
      region.style.padding = '0';
      region.style.overflow = 'hidden';
      region.style.clip = 'rect(0 0 0 0)';
      document.body.appendChild(region);
    }

    region.setAttribute('aria-hidden', false);
    region.textContent = message;
    setTimeout(() => {
      try { region.setAttribute('aria-hidden', true); region.textContent = ''; } catch (e) {}
    }, 1000);
  } catch (e) {
    console.error(`Error announcing message: ${message}`, e);
  }
}

function isDesktop() {
  return window.matchMedia('(min-width: 990px)').matches;
}

// Attach basic menubar keyboard behavior to a top-level nav container
// options: { topSelector, submenuSelector, topItemSelector }
function attachMenubarKeyboardHandlers(menubarElement, options = {}) {
  if (!menubarElement) return;

  const topItems = menubarElement.querySelectorAll(options.topItemSelector || '.top-item');

  function focusIndex(i) {
    if (!topItems[i]) return;
    topItems[i].focus();
  }

  function getIndexOf(el) {
    return Array.prototype.indexOf.call(topItems, el);
  }

  topItems.forEach((item) => {
    item.addEventListener('keydown', (event) => {
      const code = event.code.toUpperCase();
      const idx = getIndexOf(event.currentTarget);

      if (code === 'ARROWLEFT') {
        event.preventDefault();
        const prev = (idx - 1 + topItems.length) % topItems.length;
        focusIndex(prev);
      }

      if (code === 'ARROWRIGHT') {
        event.preventDefault();
        const next = (idx + 1) % topItems.length;
        focusIndex(next);
      }

      if (code === 'ARROWDOWN') {
        // Open submenu if present
        event.preventDefault();
        const menuId = event.currentTarget.getAttribute('aria-controls');
        if (menuId) {
          const menu = document.getElementById(menuId);
          const itemMain = event.currentTarget.closest('.item-main');
          if (menu) {
            // show menu - update both ARIA and visual state
            setAriaExpanded(event.currentTarget, true);
            setInertState(menu, false);
            if (itemMain) {
              itemMain.classList.add('active');
            }
            focusFirstFocusable(menu);
          }
        }
      }

      if (code === 'ENTER' || code === 'SPACE') {
        // Toggle submenu
        const menuId = event.currentTarget.getAttribute('aria-controls');
        if (menuId) {
          event.preventDefault();
          const menu = document.getElementById(menuId);
          const isExpanded = event.currentTarget.getAttribute('aria-expanded') === 'true';
          const itemMain = event.currentTarget.closest('.item-main');
          
          // Toggle both ARIA attributes and visual state (.item-main.active)
          setAriaExpanded(event.currentTarget, !isExpanded);
          setInertState(menu, isExpanded);
          if (itemMain) {
            itemMain.classList.toggle('active', !isExpanded);
          }
          
          if (!isExpanded) focusFirstFocusable(menu);
        }
      }

      if (code === 'ESCAPE') {
        const menuId = event.currentTarget.getAttribute('aria-controls');
        if (menuId) {
          const menu = document.getElementById(menuId);
          const itemMain = event.currentTarget.closest('.item-main');
          // Close menu - update both ARIA and visual state
          setAriaExpanded(event.currentTarget, false);
          setInertState(menu, true);
          if (itemMain) {
            itemMain.classList.remove('active');
          }
          event.currentTarget.focus();
        }
      }
    });
  });

  // Attach handlers for submenu keyboard navigation and focus management
  topItems.forEach((item) => {
    const menuId = item.getAttribute('aria-controls');
    const menu = menuId ? document.getElementById(menuId) : null;
    if (!menu) return;

    // When focus enters the menu, ensure aria states are correct
    menu.addEventListener('focusin', () => {
      setAriaExpanded(item, true);
      setInertState(menu, false);
    });

    // Keyboard navigation within the submenu
    menu.addEventListener('keydown', (ev) => {
      const code = ev.code.toUpperCase();
      const focusable = Array.from(menu.querySelectorAll("a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"));
      if (!focusable.length) return;
      const activeIndex = focusable.indexOf(document.activeElement);

      if (code === 'ARROWDOWN') {
        ev.preventDefault();
        const next = (activeIndex + 1) % focusable.length;
        focusable[next].focus();
        return;
      }

      if (code === 'ARROWUP') {
        ev.preventDefault();
        const prev = (activeIndex - 1 + focusable.length) % focusable.length;
        focusable[prev].focus();
        return;
      }

      if (code === 'HOME') {
        ev.preventDefault();
        focusable[0].focus();
        return;
      }

      if (code === 'END') {
        ev.preventDefault();
        focusable[focusable.length - 1].focus();
        return;
      }

      if (code === 'ESCAPE') {
        ev.preventDefault();
        const itemMain = item.closest('.item-main');
        setAriaExpanded(item, false);
        setInertState(menu, true);
        if (itemMain) {
          itemMain.classList.remove('active');
        }
        item.focus();
        return;
      }
    });

    // Close submenu when focus leaves the menu and the opener
    menu.addEventListener('focusout', (ev) => {
      // small timeout to allow next focus to settle
      setTimeout(() => {
        if (!menu.contains(document.activeElement) && document.activeElement !== item) {
          const itemMain = item.closest('.item-main');
          setAriaExpanded(item, false);
          setInertState(menu, true);
          if (itemMain) {
            itemMain.classList.remove('active');
          }
        }
      }, 100);
    });
  });
}

/**
 * W3C-based Focus Trap Implementation
 * Provides robust focus management for modals, drawers, and other containers
 */

// Track active focus traps to support nested containers
const activeFocusTraps = [];

// Ignore focus changes when we're programmatically moving focus
let ignoreFocusChanges = false;

// Enhanced focusable element detection based on W3C standards
function isFocusable(element) {
  if (!element || element.nodeType !== Node.ELEMENT_NODE) return false;
  if (element.tabIndex < 0) return false;
  if (element.disabled) return false;
  if (element.getAttribute('aria-hidden') === 'true') return false;
  
  // Check if element is visible
  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  
  switch (element.nodeName.toUpperCase()) {
    case 'A':
      return !!element.href && element.rel !== 'ignore';
    case 'INPUT':
      return element.type !== 'hidden';
    case 'BUTTON':
    case 'SELECT':
    case 'TEXTAREA':
      return true;
    default:
      return element.tabIndex >= 0;
  }
}

// Attempt to focus an element safely
function attemptFocus(element) {
  if (!isFocusable(element)) return false;
  
  ignoreFocusChanges = true;
  try {
    element.focus();
  } catch (e) {
    // Continue regardless of error
  }
  ignoreFocusChanges = false;
  
  return document.activeElement === element;
}

// Find and focus the first focusable descendant
function focusFirstDescendant(container) {
  if (!container) return false;
  
  for (let i = 0; i < container.childNodes.length; i++) {
    const child = container.childNodes[i];
    if (attemptFocus(child) || focusFirstDescendant(child)) {
      return true;
    }
  }
  return false;
}

// Find and focus the last focusable descendant
function focusLastDescendant(container) {
  if (!container) return false;
  
  for (let i = container.childNodes.length - 1; i >= 0; i--) {
    const child = container.childNodes[i];
    if (attemptFocus(child) || focusLastDescendant(child)) {
      return true;
    }
  }
  return false;
}

/**
 * FocusTrap class - manages focus containment for a specific container
 */
class FocusTrap {
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      initialFocus: options.initialFocus || null,
      returnFocus: options.returnFocus || document.activeElement,
      allowOutsideClick: options.allowOutsideClick || false,
      escapeDeactivates: options.escapeDeactivates !== false, // default true
      ...options
    };
    
    this.isActive = false;
    this.lastFocus = null;
    
    // Bind methods to maintain context
    this.handleFocus = this.handleFocus.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }
  
  activate() {
    if (this.isActive) return;
    
    // Store the currently focused element before trapping
    this.previouslyFocused = document.activeElement;
    
    // Add this trap to the active stack
    activeFocusTraps.push(this);
    this.isActive = true;
    
    // Add event listeners
    document.addEventListener('focus', this.handleFocus, true);
    document.addEventListener('keydown', this.handleKeydown, true);
    if (!this.options.allowOutsideClick) {
      document.addEventListener('click', this.handleClick, true);
    }
    
    // Set initial focus
    this.setInitialFocus();
  }
  
  deactivate(returnFocus = true) {
    if (!this.isActive) return;
    
    // Remove from active stack
    const index = activeFocusTraps.indexOf(this);
    if (index !== -1) {
      activeFocusTraps.splice(index, 1);
    }
    
    this.isActive = false;
    
    // Remove event listeners
    document.removeEventListener('focus', this.handleFocus, true);
    document.removeEventListener('keydown', this.handleKeydown, true);
    document.removeEventListener('click', this.handleClick, true);
    
    // Return focus if requested
    if (returnFocus) {
      const focusTarget = this.options.returnFocus || this.previouslyFocused;
      if (focusTarget && attemptFocus(focusTarget)) {
        // Focus was successfully returned
      }
    }
  }
  
  setInitialFocus() {
    const initialFocus = this.options.initialFocus;
    
    if (initialFocus) {
      if (typeof initialFocus === 'string') {
        const element = this.container.querySelector(initialFocus);
        if (element && attemptFocus(element)) return;
      } else if (attemptFocus(initialFocus)) {
        return;
      }
    }
    
    // Fallback to first focusable element in container
    if (!focusFirstDescendant(this.container)) {
      // If no focusable elements, focus the container itself if possible
      attemptFocus(this.container);
    }
  }
  
  handleFocus(event) {
    if (ignoreFocusChanges) return;
    
    // Check if focus is within our container
    if (this.container.contains(event.target)) {
      this.lastFocus = event.target;
    } else {
      // Focus escaped - bring it back
      if (this.lastFocus && attemptFocus(this.lastFocus)) {
        return;
      }
      
      // Try to focus first element
      if (!focusFirstDescendant(this.container)) {
        // If that fails, try last element
        focusLastDescendant(this.container);
      }
      
      this.lastFocus = document.activeElement;
    }
  }
  
  handleKeydown(event) {
    if (event.key === 'Escape' && this.options.escapeDeactivates) {
      event.preventDefault();
      event.stopPropagation();
      this.deactivate();
      return;
    }
    
    // Handle Tab key cycling
    if (event.key === 'Tab') {
      this.handleTabKey(event);
    }
  }
  
  handleTabKey(event) {
    // Get all focusable elements in the container
    const focusableElements = this.getFocusableElements();
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const currentElement = document.activeElement;
    
    // If shift+tab on first element, go to last
    if (event.shiftKey && currentElement === firstElement) {
      event.preventDefault();
      attemptFocus(lastElement);
      return;
    }
    
    // If tab on last element, go to first
    if (!event.shiftKey && currentElement === lastElement) {
      event.preventDefault();
      attemptFocus(firstElement);
      return;
    }
  }
  
  getFocusableElements() {
    if (!this.container) return [];
    
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([type="hidden"]):not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      'details summary',
      '[contenteditable="true"]'
    ];
    
    const candidates = this.container.querySelectorAll(focusableSelectors.join(', '));
    return Array.from(candidates).filter(element => isFocusable(element));
  }
  
  handleClick(event) {
    if (!this.container.contains(event.target)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }
}

// Simplified API functions for backwards compatibility
function trapFocus(container, initialFocus, options = {}) {
  if (!container) return null;
  
  // Remove any existing trap for this container
  removeFocusTrap(container);
  
  const focusTrap = new FocusTrap(container, {
    initialFocus,
    ...options
  });
  
  focusTrap.activate();
  
  // Store trap reference on the container for easy removal
  container._focusTrap = focusTrap;
  
  return focusTrap;
}

function removeFocusTrap(container, returnFocus = true) {
  if (!container) {
    // Remove the most recent trap if no container specified
    const lastTrap = activeFocusTraps[activeFocusTraps.length - 1];
    if (lastTrap) {
      lastTrap.deactivate(returnFocus);
    }
    return;
  }
  
  if (container._focusTrap) {
    container._focusTrap.deactivate(returnFocus);
    delete container._focusTrap;
  }
}

function createFocusTrap(container, options = {}) {
  return new FocusTrap(container, options);
}

// Enhanced version of existing focusFirstFocusable that uses new isFocusable logic
function focusFirstFocusableEnhanced(container) {
  if (!container) return null;
  return focusFirstDescendant(container) ? document.activeElement : null;
}

function focusLastFocusableEnhanced(container) {
  if (!container) return null;
  return focusLastDescendant(container) ? document.activeElement : null;
}

// Expose helpers on the global object for non-module theme assets to consume
window.A11Y = window.A11Y || {};
// Merge helpers into any existing A11Y object without overwriting other properties
Object.assign(window.A11Y, {
  setAriaExpanded,
  setAriaHidden,
  setInertState,
  focusFirstFocusable,
  focusLastFocusable,
  focusFirstFocusableEnhanced,
  focusLastFocusableEnhanced,
  attachMenubarKeyboardHandlers,
  isDesktop,
  announce,
  // New focus trap utilities
  trapFocus,
  removeFocusTrap,
  createFocusTrap,
  isFocusable,
  attemptFocus,
  FocusTrap
});

// Backwards-compatible default export placeholder
window.A11Y.default = window.A11Y;



/**
 * Scanner-friendly accessibility helpers for generated/PageFly content.
 * These only add missing semantics; they do not change visual behaviour.
 */
function enhanceGeneratedContentAccessibility() {
  // PageFly outputs paragraph checkboxes with visible sibling text but no
  // programmatic label. Reference that existing visible text instead of
  // inventing a separate aria-label.
  document.querySelectorAll('input[type="checkbox"][id^="compact-"]').forEach((input) => {
    const alreadyLabelled =
      (input.labels && input.labels.length > 0) ||
      input.hasAttribute('aria-label') ||
      input.hasAttribute('aria-labelledby');

    if (alreadyLabelled) return;

    const labelText = input.nextElementSibling;
    if (!labelText || !labelText.textContent.trim()) return;

    if (!labelText.id) {
      labelText.id = `${input.id}-label`;
    }

    input.setAttribute('aria-labelledby', labelText.id);
  });

  // PageFly images sometimes have useful title text but no alt attribute.
  // Reuse that existing author-provided text rather than inventing image descriptions.
  document.querySelectorAll('.__pf img, [data-pf-type] img').forEach((image) => {
    const currentAlt = image.getAttribute('alt');
    const title = (image.getAttribute('title') || '').trim();

    if ((currentAlt === null || !currentAlt.trim()) && title) {
      image.setAttribute('alt', title);
    }
  });

  // PageFly can output image-only links with no accessible name. Where the
  // linked image already has meaningful alt/title text, use it to name the link.
  document.querySelectorAll('a[data-pf-type="Image2"], a[data-pf-type="Image"]').forEach((link) => {
    const hasAccessibleName =
      link.hasAttribute('aria-label') ||
      link.hasAttribute('aria-labelledby') ||
      link.textContent.trim();

    if (hasAccessibleName) return;

    const image = link.querySelector('img');
    if (!image) return;

    const imageText =
      (image.getAttribute('alt') || '').trim() ||
      (image.getAttribute('title') || '').trim();

    if (imageText) {
      link.setAttribute('aria-label', imageText);
    }
  });

  // PageFly accordion/utility SVGs and Ionicons are decorative when they sit
  // alongside visible control text. Hide them from assistive technology.
  document.querySelectorAll('svg.pf-accordion-icon, svg.ionicon').forEach((svg) => {
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('role', 'presentation');
  });

  // Automatically playing hero/background videos need a user control when
  // they continue for more than five seconds. Add a visible pause/play button
  // without changing the PageFly/custom-liquid source that owns the video.
  document.querySelectorAll('video.fm-hero-bg--video[autoplay]').forEach((video) => {
    if (video.hasAttribute('data-a11y-video-control')) return;

    video.setAttribute('data-a11y-video-control', '');
    video.setAttribute('aria-hidden', 'true');

    const banner = video.closest('.fm-hero-banner');
    if (!banner) return;

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'fm-hero-video-toggle button-brand button-brand--white-secondary-outline';
    toggle.textContent = 'Pause video';
    toggle.style.position = 'absolute';
    toggle.style.right = '1.5rem';
    toggle.style.bottom = '1.5rem';
    toggle.style.zIndex = '4';
    toggle.style.minWidth = '44px';
    toggle.style.minHeight = '44px';

    const syncToggleText = () => {
      toggle.textContent = video.paused ? 'Play video' : 'Pause video';
    };

    toggle.addEventListener('click', () => {
      if (video.paused) {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      } else {
        video.pause();
      }
      syncToggleText();
    });

    video.addEventListener('play', syncToggleText);
    video.addEventListener('pause', syncToggleText);

    const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotion && reducedMotion.matches) {
      video.pause();
    }

    // The mobile layout replaces this video with a static image. Keep the
    // pause/play control available only while the video itself is rendered.
    const syncToggleVisibility = () => {
      const styles = window.getComputedStyle(video);
      const videoIsVisible =
        styles.display !== 'none' &&
        styles.visibility !== 'hidden' &&
        video.getClientRects().length > 0;

      toggle.hidden = !videoIsVisible;
      if (videoIsVisible) {
        toggle.style.removeProperty('display');
      } else {
        toggle.style.display = 'none';
      }
    };

    syncToggleText();
    video.insertAdjacentElement('afterend', toggle);
    syncToggleVisibility();
    window.addEventListener('resize', syncToggleVisibility, { passive: true });
  });

  // PageFly video cover buttons can render as icon/image-only buttons. Give
  // the button a stable accessible name while leaving PageFly behaviour alone.
  document.querySelectorAll('button.pf-video-cover-button').forEach((button) => {
    if (
      button.hasAttribute('aria-label') ||
      button.hasAttribute('aria-labelledby') ||
      button.textContent.trim()
    ) {
      return;
    }

    const coverImage = button.querySelector('img');
    const coverAlt = coverImage ? (coverImage.getAttribute('alt') || '').trim() : '';
    const genericPageFlyAlt = /^PageFly HTMLVideo cover$/i.test(coverAlt);

    button.setAttribute(
      'aria-label',
      coverAlt && !genericPageFlyAlt ? `Play video: ${coverAlt}` : 'Play video'
    );
  });

  // Legal/static content can contain generic "here" links created in Shopify page
  // content. Give only the known scanner-flagged destinations a descriptive
  // accessible name without changing their visible copy.
  const descriptiveLinkLabels = [
    ['central.com/central-privacy-form', 'Open privacy request form'],
    ['/pages/ccpa-compliance', 'Submit a CCPA privacy request'],
    ['/pages/gdpr-compliance', 'Submit a GDPR privacy request'],
    ['/pages/cookie-policy', 'Read the Cookie Policy'],
    ['/pages/contact-us', 'Contact Ferry-Morse support'],
    ['/pages/how-to-contact-us', 'Contact Ferry-Morse support'],
    ['/blogs/the-greenhouse/how-to-grow-your-own-backyard-flower-garden', 'Read How to Grow Your Own Backyard Flower Garden'],
    ['/pages/rewards', 'Learn about Ferry-Morse Rewards'],
    ['/blogs/the-greenhouse/flower-seed-mixes-a-guide-to-effortless-beauty', 'Read Flower Seed Mixes: A Guide to Effortless Beauty']
  ];

  document.querySelectorAll('a[href]').forEach((link) => {
    const visibleText = link.textContent.trim().toLowerCase();
    if (!['here', 'click here', 'read more', 'learn more'].includes(visibleText)) return;
    if (link.hasAttribute('aria-label') || link.hasAttribute('aria-labelledby')) return;

    const href = link.getAttribute('href') || '';
    const match = descriptiveLinkLabels.find(([fragment]) => href.includes(fragment));
    if (match) {
      link.setAttribute('aria-label', match[1]);
    }
  });

  // CartCoders flags a handful of inline content colors on legal/accessibility
  // pages as below WCAG AA contrast. These are Shopify page-content inline styles,
  // so correct only the known scanner-flagged values on the affected pages.
  if (
    window.location.pathname === '/pages/ferry-morse-privacy-statement' ||
    window.location.pathname === '/pages/cookie-policy'
  ) {
    document.querySelectorAll('[style]').forEach((element) => {
      const inlineColor = (element.style.color || '').replace(/\s+/g, '').toLowerCase();
      if (inlineColor === 'rgb(249,162,37)' || inlineColor === '#f9a225') {
        element.style.color = '#8c5100';
      }
    });
  }

  if (window.location.pathname === '/pages/accessibility-statement') {
    document.querySelectorAll('[style]').forEach((element) => {
      const inlineColor = (element.style.color || '').replace(/\s+/g, '').toLowerCase();
      if (inlineColor === 'rgb(41,82,53)' || inlineColor === '#295235') {
        element.style.color = '#0b2413';
      }
    });
  }

  // The privacy statement contains an HTML table that CartCoders identifies as
  // a layout table. Remove data-table semantics from that one table and its
  // structural descendants rather than forcing header relationships that do
  // not describe the content.
  if (window.location.pathname === '/pages/ferry-morse-privacy-statement') {
    document.querySelectorAll('table[width="100%"]').forEach((table) => {
      if (table.querySelector('th')) return;

      table.setAttribute('role', 'presentation');
      table.querySelectorAll('thead, tbody, tfoot, tr, td').forEach((element) => {
        element.setAttribute('role', 'presentation');
      });
    });

    // Some privacy-statement paragraphs were authored as <ol> elements with no
    // <li> children. They are not lists, so restore paragraph semantics while
    // preserving the existing content and styling hooks.
    document.querySelectorAll('ol').forEach((list) => {
      if (list.querySelector(':scope > li')) return;
      if (!list.textContent.trim()) return;

      const paragraph = document.createElement('p');
      paragraph.className = list.className;
      paragraph.innerHTML = list.innerHTML;
      list.replaceWith(paragraph);
    });
  }


  // PageFly shipping/returns content contains real data tables whose header
  // cells omit scope. Add native column-header relationships without changing
  // the table content or layout.
  if (window.location.pathname === '/pages/shipping-returns-policy') {
    document.querySelectorAll('[data-pf-type="Table"] table').forEach((table) => {
      table.querySelectorAll('tr').forEach((row) => {
        row.querySelectorAll(':scope > th').forEach((header) => {
          if (!header.hasAttribute('scope')) {
            header.setAttribute('scope', 'col');
          }
        });
      });
    });

    // CartCoders treats every <i> as an icon-font element, even where PageFly
    // uses <i> for authored italic text. Preserve the intended emphasis with
    // <em>; genuinely empty <i> elements are decorative and can be hidden.
    document.querySelectorAll('#MainContent i').forEach((italic) => {
      if (!italic.textContent.trim()) {
        italic.setAttribute('aria-hidden', 'true');
        return;
      }

      const emphasis = document.createElement('em');
      [...italic.attributes].forEach((attribute) => {
        emphasis.setAttribute(attribute.name, attribute.value);
      });
      while (italic.firstChild) emphasis.appendChild(italic.firstChild);
      italic.replaceWith(emphasis);
    });
  }

  // PageFly can render the same product more than once on a landing page.
  // Shopify's product form tag then generates the same form id each time, which
  // CartCoders flags as duplicate form IDs. Keep the first ID unchanged and make
  // only later duplicates unique. Any controls inside the same ProductBox that
  // explicitly point at the original form id are updated with it.
  const pageFlyFormIdCounts = new Map();
  document.querySelectorAll('form.pf-product-form[id]').forEach((form) => {
    const originalId = form.id;
    const count = (pageFlyFormIdCounts.get(originalId) || 0) + 1;
    pageFlyFormIdCounts.set(originalId, count);

    if (count === 1) return;

    const uniqueId = `${originalId}-a11y-${count}`;
    form.setAttribute('data-a11y-original-id', originalId);
    form.id = uniqueId;

    const productBox = form.closest('[data-pf-type="ProductBox"]') || form.parentElement;
    if (productBox) {
      productBox.querySelectorAll('[form]').forEach((control) => {
        if (control.getAttribute('form') === originalId) {
          control.setAttribute('form', uniqueId);
        }
      });
    }
  });

  // A few generated/content pages contain empty heading elements used only as
  // layout spacers. Preserve their classes/styles and children, but remove
  // heading semantics so they no longer create empty outline entries.
  document.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((heading) => {
    const hasText = heading.textContent.trim();
    const hasAccessibleName =
      (heading.getAttribute('aria-label') || '').trim() ||
      (heading.getAttribute('aria-labelledby') || '').trim();

    if (hasText || hasAccessibleName) return;

    const replacement = document.createElement('div');
    [...heading.attributes].forEach((attribute) => {
      replacement.setAttribute(attribute.name, attribute.value);
    });
    replacement.setAttribute('data-a11y-replaced-empty-heading', heading.tagName.toLowerCase());
    while (heading.firstChild) replacement.appendChild(heading.firstChild);
    heading.replaceWith(replacement);
  });

  // The Safe Seed Pledge page contains one page-builder image with no alt at
  // all. Give that otherwise unnamed content image a page-specific fallback.
  if (window.location.pathname === '/pages/safe-seed-pledge') {
    document.querySelectorAll('#MainContent img:not([alt])').forEach((image) => {
      image.setAttribute('alt', 'Ferry-Morse Safe Seed Pledge');
    });
  }

  // The Safe Seed Pledge page contains one scanner-flagged inline 28px text
  // size. Use the equivalent relative unit so browser text zoom can scale it.
  if (window.location.pathname === '/pages/safe-seed-pledge') {
    document.querySelectorAll('[style]').forEach((element) => {
      if (element.style.fontSize === '28px') {
        element.style.fontSize = '1.75rem';
      }
    });
  }

  // A number of PageFly/static pages in the CartCoders scan have no H1 at all.
  // Add a visually-hidden page heading only when the rendered page genuinely
  // has no H1, so pages that already provide one are untouched.
  if (
    document.body &&
    document.body.className.indexOf('template-page') !== -1 &&
    !document.querySelector('h1')
  ) {
    const main = document.querySelector('#MainContent') || document.querySelector('main');

    if (main) {
      const ogTitle = document.querySelector('meta[property="og:title"]');
      const pageTitle =
        (ogTitle && ogTitle.getAttribute('content') && ogTitle.getAttribute('content').trim()) ||
        document.title.replace(/\s*[–—|]\s*Ferry-Morse.*$/i, '').trim() ||
        'Page';

      const heading = document.createElement('h1');
      heading.className = 'visually-hidden';
      heading.setAttribute('data-a11y-generated-page-heading', '');
      heading.textContent = pageTitle;
      main.insertBefore(heading, main.firstChild);
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', enhanceGeneratedContentAccessibility, { once: true });
} else {
  enhanceGeneratedContentAccessibility();
}
